import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import DownloadIcon from "@mui/icons-material/Download";
import OpenInFullIcon from "@mui/icons-material/OpenInFull";
import SearchIcon from "@mui/icons-material/Search";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { endOfDay, startOfDay } from "date-fns";
import { useSnackbar } from "notistack";
import { useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { ExcelExportButton } from "@/components/export/ExcelExportButton";
import { DateRangeSelect } from "@/components/input/DateRangeSelect";
import { ModalType, useModal } from "@/components/ModalProvider";
import { useAddDeviceHistoryEntry, useDeviceHistory } from "@/hooks/addins/useDeviceHistory";
import { formatDate, useFormattedDate } from "@/hooks/metrics/useFormattedDate";
import { DeviceHistoryEntry } from "@/models/DeviceHistoryEntry";
import { Report } from "@/utilities/export/report";
import { downloadBlob } from "@/utilities/xlsx";

function HistoryEntryItem({ entry }: { entry: DeviceHistoryEntry }) {
  const formattedDate = useFormattedDate(entry.createdAt);
  return (
    <ListItem sx={{ alignItems: "flex-start" }}>
      <ListItemText primary={entry.message} secondary={`${entry.createdBy} — ${formattedDate}`} />
    </ListItem>
  );
}

interface DeviceHistoryProps {
  deviceId: string;
  deviceName?: string;
  fullscreen?: boolean;
}

export default function DeviceHistory({
  deviceId,
  deviceName,
  fullscreen = false,
}: DeviceHistoryProps) {
  const { t, i18n } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const { data: entries, isLoading, isError } = useDeviceHistory(deviceId);
  const { addEntry, isPending } = useAddDeviceHistoryEntry();
  const { openModal } = useModal();
  const [message, setMessage] = useState("");
  const [search, setSearch] = useState("");
  const [searchOpen, setSearchOpen] = useState(false);
  const [dateFrom, setDateFrom] = useState<Date | undefined>(undefined);
  const [dateTo, setDateTo] = useState<Date | undefined>(undefined);
  const [exportAnchorEl, setExportAnchorEl] = useState<HTMLElement | null>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  const handleAdd = () => {
    const trimmed = message.trim();
    if (!trimmed) return;
    addEntry(deviceId, trimmed);
    setMessage("");
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleAdd();
    }
  };

  const handleToggleSearch = () => {
    if (searchOpen) {
      setSearch("");
      setDateFrom(undefined);
      setDateTo(undefined);
      setSearchOpen(false);
    } else {
      setSearchOpen(true);
      setTimeout(() => searchRef.current?.focus(), 0);
    }
  };

  const filteredEntries = useMemo(() => {
    let result = entries.filter(e => e.type !== "clearRemark");

    if (search.trim()) {
      const lower = search.toLowerCase();
      result = result.filter(
        e => e.message.toLowerCase().includes(lower) || e.createdBy.toLowerCase().includes(lower),
      );
    }

    if (dateFrom) {
      const from = startOfDay(dateFrom).getTime();
      result = result.filter(e => new Date(e.createdAt).getTime() >= from);
    }

    if (dateTo) {
      const to = endOfDay(dateTo).getTime();
      result = result.filter(e => new Date(e.createdAt).getTime() <= to);
    }

    return result;
  }, [entries, search, dateFrom, dateTo]);

  const baseName = deviceName
    ? `${deviceName} - ${t("deviceHistory.title")}`
    : t("deviceHistory.title");

  const buildRows = <D extends string | Date>(dateCell: (value: string) => D): (string | D)[][] => {
    const headers = [
      t("deviceHistory.export.date"),
      t("deviceHistory.export.message"),
      t("deviceHistory.export.author"),
      t("deviceHistory.export.type"),
    ];
    return [
      headers,
      ...filteredEntries.map(e => [
        dateCell(e.createdAt),
        e.message,
        e.createdBy,
        e.type ?? "remark",
      ]),
    ];
  };

  // A real Date cell, not formatted text — Excel then sorts and filters it as a date.
  const [excelHeaders, ...excelRows] = buildRows(value => new Date(value));
  const excelReport: Report = {
    // The device in the title keeps file names distinct per device.
    title: baseName,
    filter: deviceName,
    ...((dateFrom || dateTo) && {
      period: {
        from: dateFrom && startOfDay(dateFrom),
        to: dateTo && endOfDay(dateTo),
      },
    }),
    table: {
      columns: excelHeaders.map(header => ({ field: String(header), header: String(header) })),
      rows: excelRows,
    },
  };

  const handleExportCsv = () => {
    try {
      // The app's language, not the browser's — the two differ often enough to matter.
      const rows = buildRows(value => formatDate(value, i18n.language));
      const csv = rows
        .map(row =>
          row
            .map(cell => {
              const escaped = cell.replace(/"/g, '""');
              return /[",\n\r]/.test(escaped) ? `"${escaped}"` : escaped;
            })
            .join(","),
        )
        .join("\r\n");
      const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
      downloadBlob(blob, `${baseName}.csv`);
    } catch (error) {
      console.error(error);
      enqueueSnackbar(t("common.exportError"), { variant: "error" });
    } finally {
      setExportAnchorEl(null);
    }
  };

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 2 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, p: 1 }}>
        {searchOpen ? (
          <TextField
            inputRef={searchRef}
            size="small"
            fullWidth
            placeholder={t("deviceHistory.search")}
            value={search}
            onChange={e => setSearch(e.target.value)}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              },
            }}
          />
        ) : (
          <TextField
            size="small"
            fullWidth
            multiline
            maxRows={3}
            placeholder={t("deviceHistory.placeholder")}
            value={message}
            onChange={e => setMessage(e.target.value)}
            onKeyDown={handleKeyDown}
            disabled={isPending}
          />
        )}
        {!searchOpen && (
          <IconButton
            color="primary"
            onClick={handleAdd}
            disabled={!message.trim() || isPending}
            size="small"
          >
            <AddIcon />
          </IconButton>
        )}
        <Tooltip title={searchOpen ? t("common.close") : t("deviceHistory.search")}>
          <IconButton
            size="small"
            onClick={handleToggleSearch}
            color={searchOpen ? "primary" : "default"}
          >
            {searchOpen ? <CloseIcon /> : <SearchIcon />}
          </IconButton>
        </Tooltip>
        {!fullscreen && (
          <Tooltip title={t("common.expand")}>
            <IconButton
              size="small"
              onClick={() => openModal({ deviceId, deviceName }, ModalType.DEVICE_HISTORY)}
            >
              <OpenInFullIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
        {fullscreen && (
          <>
            <ExcelExportButton
              report={excelReport}
              disabled={isError || filteredEntries.length === 0}
            />
            <Tooltip title={t("deviceHistory.export.button")}>
              <span>
                <IconButton
                  size="small"
                  onClick={e => setExportAnchorEl(e.currentTarget)}
                  disabled={filteredEntries.length === 0}
                >
                  <DownloadIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <Menu
              anchorEl={exportAnchorEl}
              open={Boolean(exportAnchorEl)}
              onClose={() => setExportAnchorEl(null)}
            >
              <MenuItem onClick={handleExportCsv}>{t("deviceHistory.export.csv")}</MenuItem>
            </Menu>
          </>
        )}
      </Box>
      {fullscreen && searchOpen && (
        <Box sx={{ px: 1, pb: 1 }}>
          <DateRangeSelect
            fromDate={dateFrom}
            toDate={dateTo}
            onChange={(from, to) => {
              setDateFrom(from);
              setDateTo(to);
            }}
          />
        </Box>
      )}
      {filteredEntries.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ p: 2, textAlign: "center" }}>
          {entries.length === 0 ? t("deviceHistory.noEntries") : t("deviceHistory.noResults")}
        </Typography>
      ) : (
        <List dense sx={{ overflow: "auto", flex: 1 }}>
          {filteredEntries.map(entry => (
            <HistoryEntryItem key={entry.id} entry={entry} />
          ))}
        </List>
      )}
    </Box>
  );
}
